import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  computed,
  forwardRef,
  inject,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-searchable-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true,
    },
  ],
  template: `
    <div class="select">
      @if (label) {
        <label class="select-label">{{ label }}</label>
      }
      <button
        type="button"
        class="select-trigger"
        [class.open]="open()"
        [disabled]="disabled()"
        (click)="toggle()">
        <span class="select-value" [class.placeholder]="!selectedLabel()">
          {{ selectedLabel() || placeholder || ('common.select' | translate) }}
        </span>
        <span class="material-symbols-outlined">expand_more</span>
      </button>

      @if (open()) {
        <div class="select-panel">
          <input
            type="text"
            class="select-search"
            [value]="query()"
            (input)="onQuery($event)"
            placeholder="{{ 'common.search' | translate }}" />
          <div class="select-options">
            @if (filteredOptions().length === 0) {
              <div class="select-empty">{{ 'common.noResults' | translate }}</div>
            } @else {
              @for (option of filteredOptions(); track option.value) {
                <button
                  type="button"
                  class="select-option"
                  [class.active]="option.value === value()"
                  (click)="select(option.value)">
                  {{ option.label }}
                </button>
              }
            }
          </div>
        </div>
      }
    </div>
  `,
  styles: `
    .select {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }

    .select-label {
      font-size: 0.8125rem;
      font-weight: 500;
      color: oklch(0.35 0 0);
    }

    .select-trigger {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      width: 100%;
      padding: 0.5rem 0.75rem;
      border: 1px solid oklch(0.85 0 0);
      border-radius: 0.375rem;
      background: white;
      font-size: 0.875rem;
      cursor: pointer;
      transition: border-color 0.15s, box-shadow 0.15s;
      text-align: left;
    }

    .select-trigger.open {
      border-color: oklch(0.5 0.2 250);
      box-shadow: 0 0 0 3px oklch(0.5 0.2 250 / 0.15);
    }

    .select-trigger:disabled {
      background: oklch(0.97 0 0);
      color: oklch(0.5 0 0);
      cursor: not-allowed;
    }

    .select-value {
      flex: 1;
      text-align: left;
    }

    .select-value.placeholder {
      color: oklch(0.5 0 0);
    }

    .select-panel {
      position: absolute;
      top: calc(100% + 0.5rem);
      left: 0;
      right: 0;
      z-index: 20;
      background: white;
      border: 1px solid oklch(0.9 0 0);
      border-radius: 0.5rem;
      box-shadow: 0 20px 35px -20px rgb(0 0 0 / 0.35);
      padding: 0.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      max-height: 280px;
    }

    .select-search {
      width: 100%;
      padding: 0.5rem 0.75rem;
      border: 1px solid oklch(0.88 0 0);
      border-radius: 0.375rem;
      font-size: 0.875rem;
    }

    .select-search:focus {
      outline: none;
      border-color: oklch(0.5 0.2 250);
      box-shadow: 0 0 0 3px oklch(0.5 0.2 250 / 0.12);
    }

    .select-options {
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .select-option {
      display: flex;
      align-items: center;
      padding: 0.5rem 0.75rem;
      border: none;
      background: transparent;
      border-radius: 0.375rem;
      font-size: 0.875rem;
      cursor: pointer;
      text-align: left;
    }

    .select-option:hover {
      background: oklch(0.95 0 0);
    }

    .select-option.active {
      background: oklch(0.92 0.04 250);
      color: oklch(0.35 0.18 250);
      font-weight: 600;
    }

    .select-empty {
      padding: 0.5rem 0.75rem;
      font-size: 0.8125rem;
      color: oklch(0.5 0 0);
    }
  `,
})
export class SearchableSelectComponent implements ControlValueAccessor {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  private readonly _options = signal<SearchableSelectOption[]>([]);
  protected readonly open = signal(false);
  protected readonly query = signal('');
  protected readonly value = signal('');
  protected readonly disabled = signal(false);

  @Input() label = '';
  @Input() placeholder = 'Select...';
  @Input() allowEmpty = false;
  @Input() emptyLabel = 'None';

  @Input() set options(value: SearchableSelectOption[]) {
    this._options.set(value ?? []);
  }

  protected readonly filteredOptions = computed(() => {
    const search = this.query().trim().toLowerCase();
    const base = this._options();
    const options = this.allowEmpty
      ? [{ value: '', label: this.emptyLabel }, ...base]
      : base;
    if (!search) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(search)
    );
  });

  protected readonly selectedLabel = computed(() => {
    const currentValue = this.value();
    if (!currentValue) {
      return this.allowEmpty && currentValue === '' ? this.emptyLabel : '';
    }
    return this._options().find((option) => option.value === currentValue)?.label ?? '';
  });

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: MouseEvent): void {
    if (!this.open()) return;
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.open.set(false);
      this.query.set('');
      this.onTouched();
    }
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected toggle(): void {
    if (this.disabled()) return;
    this.open.set(!this.open());
    if (this.open()) {
      this.query.set('');
    }
  }

  protected onQuery(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.query.set(input.value);
  }

  protected select(value: string): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
    this.open.set(false);
    this.query.set('');
  }
}
